import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import { fillTimes, ONE_DAY, ONE_HOUR, ONE_WEEK } from '../utils/date';

export type OptionMessage = {
  value: string | null;
  title?: MessageDescriptor;
  description?: MessageDescriptor | string;
  warning?: MessageDescriptor;
  disabled?: boolean;
};

export const reasonMessages = defineMessages({
  '0': { defaultMessage: 'Not specified', id: 'Rk7UyM' },
  '1': { defaultMessage: 'Defamatory', id: 'ww4hnQ' },
  '2': { defaultMessage: 'Aggression', id: 'rG/Ich' },
  '3': { defaultMessage: 'Insult', id: 'm5z+hx' },
  '4': { defaultMessage: 'Racism', id: 'r2Bm3u' },
  '5': { defaultMessage: 'Illegal', id: '219pX9' },
  '6': { defaultMessage: 'Vulgar nickname', id: 'DMEq85' },
  '7': { defaultMessage: 'Privacy', id: 'cXBJ7U' },
  '8': { defaultMessage: 'SPAM', id: 'MROph7' },
  '9': { defaultMessage: 'Advertising', id: 'cS3oUQ' },
  '10': { defaultMessage: 'Pedophilia', id: 'b2tu2f' },
  '11': { defaultMessage: 'Pornography', id: '12YBdo' },
  '12': { defaultMessage: 'Jokes', id: 'eygMvw' },
  '13': { defaultMessage: 'Suicide', id: 'gj8mBa' },
  '14': { defaultMessage: 'Illegible', id: 's0Y9oP' },
  '15': { defaultMessage: 'Prostitution', id: '8s30BR' },
  '16': { defaultMessage: 'Respect for victims', id: 'nZblXO' },
  '17': { defaultMessage: 'Religious proselytism', id: 't+KGul' },
  '18': { defaultMessage: 'Religious insult', id: 'kEtNQr' },
  '19': { defaultMessage: 'Copyright', id: 'Kvo3A0' },
  '20': { defaultMessage: 'Duplicate', id: '4fHiNl' },
  '21': { defaultMessage: 'Link to a prohibited website', id: '+nHlMl' },
  '22': { defaultMessage: 'Incitement to violence/crime/theft', id: '9V/lDt' },
  '23': { defaultMessage: 'Homophobia', id: 'Ou599Z' },
  '24': { defaultMessage: 'Vulgarity', id: '7qN6YX' },
  '99': { defaultMessage: 'Other reason', id: 'HBnygC' },
});

export const REASONS_OPTIONS = Object.entries(reasonMessages).map(([value, label]) => ({
  value,
  label,
}));

export const intl = defineMessages({
  banTitle: { defaultMessage: 'Banishment', id: 'FkjMRF' },
  refuseSource: { defaultMessage: 'Delete the message', id: '3Rbukn' },
  // Le contenu source est un SUJET quand le bannissement est lancé depuis un sujet :
  // parler du « message » y serait faux.
  refuseSourceSubject: { defaultMessage: 'Delete the topic', id: 'oAiYrv' },
  refuseAll: { defaultMessage: 'Delete all their messages', id: 'aFsXcC' },
  // Symétrique de refuseAll : la case est indépendante, « also » y sonnait faux.
  refuseSubjects: { defaultMessage: 'Delete all their topics', id: 'NyOQ+9' },
  userProfile: { defaultMessage: 'User profile', id: 'F/REeu' },
  moreBanRules: { defaultMessage: 'Banning rules', id: 'fFDuhj' },
  save: { defaultMessage: 'Save', id: 'jvo0vs' },
  reasonDesc: { defaultMessage: 'Reason description:', id: 'B93NlR' },
  noIp: { defaultMessage: 'No IP address associated with this user', id: 'hvnvCz' },
});

export const intlErrors = defineMessages({
  type: { defaultMessage: 'Please select at least one ban rule', id: 'QZ6zM2' },
  reason: {
    defaultMessage: 'Please indicate the reason and rationale for the ban',
    id: 'PTNeuJ',
  },
  noUser: { defaultMessage: 'No user selected', id: 'HLpTpV' },
  duration: { defaultMessage: 'Reserved for Premium accounts', id: 'qzlXlE' },
});

const intlModes = defineMessages({
  permanent: { defaultMessage: 'Permanent ban', id: 'piMFX/' },
  temporary: { defaultMessage: 'Temporary ban', id: 'r2jxBD' },
  permanentDescription: {
    defaultMessage:
      'Users permanently banned will no longer be able to post or vote on comments on your site.',
    id: 'nFlhoM',
  },
  temporaryDescription: {
    defaultMessage:
      'Users temporarily banned will not be able to post or vote on comments on your site for a period of your choice. They will receive a notification of their suspension and its duration.',
    id: 'ZW3UKX',
  },
  temporaryDisabled: { defaultMessage: 'Premium Feature', id: 'dDIBn4' },
});
export const BAN_MODES = [
  { value: 'permanent', title: intlModes.permanent, description: intlModes.permanentDescription },
  {
    value: 'temporary',
    title: intlModes.temporary,
    description: intlModes.temporaryDescription,
    warning: intlModes.temporaryDisabled,
  },
] as const;

export type BanModeKey = (typeof BAN_MODES)[number]['value'] | '';

const intlOptions = defineMessages({
  shadow: { defaultMessage: 'Shadow ban', id: 'zyWSPQ' },
  noBanMessage: { defaultMessage: 'Do not inform user', id: '6XjxBX' },
  shadowDescription: {
    defaultMessage:
      'Users ghost banned will not know they are banned. They will still be able to post, vote on comments on your site but all new comments they post will no longer be visible to anyone except themselves and moderators.',
    id: 'xiLAld',
  },
  noBanMessageDescription: {
    defaultMessage:
      'No message will be displayed to the user, and all new topics and messages will be systematically deleted after posting.',
    id: 'Ws2SfO',
  },
  shadowDisabled: {
    defaultMessage: 'Sending a message to the user is not compatible with ghost banning',
    id: 'q702x2',
  },
  noBanMessageDisabled: {
    defaultMessage: 'Ghost banning automatically disables message sending to the user',
    id: 'Lq4lle',
  },
  noOptionSelected: {
    defaultMessage:
      'A message is displayed in the editing area informing the user of the ban and its duration.',
    id: 'H7Ya9H',
  },
});
export const BAN_OPTIONS = [
  {
    value: 'noBanMessage',
    title: intlOptions.noBanMessage,
    description: intlOptions.noBanMessageDescription,
    warning: intlOptions.noBanMessageDisabled,
  },
  {
    value: 'shadow',
    title: intlOptions.shadow,
    description: intlOptions.shadowDescription,
    warning: intlOptions.shadowDisabled,
  },
  {
    value: null,
    description: intlOptions.noOptionSelected,
  },
] as const;

export type BanOptionKey = (typeof BAN_OPTIONS)[number]['value'] | '';

const intlDateRanges = defineMessages({
  permanently: { defaultMessage: 'Permanently', id: 'mcjozZ' },
  days: {
    defaultMessage: '{length,plural,zero{Permanently} one{day} other{days}}',
    id: 'aoD7d6',
  },
  weeks: {
    defaultMessage: '{length,plural,zero{week} one{week} other{weeks}}',
    id: 'krHSb5',
  },
  hours: {
    defaultMessage: '{length,plural,zero{hour} one{hour} other{hours}}',
    id: 'z3PC9+',
  },
  months: { defaultMessage: '{length,plural,zero{month} one{month} other{months}}', id: 'qvr6Ov' },
});

/* { unit: number; length: number; } | { length: number; base: number; } */
export interface BanDateRange {
  unit?: number;
  label?: MessageDescriptor;
  length: number;
  custom?: boolean;
  base?: number;
}

export const BAN_DATERANGES = [
  { unit: 0, label: intlDateRanges.permanently, length: 0, custom: true },
  ...fillTimes<MessageDescriptor>(23, { unit: ONE_HOUR, label: intlDateRanges.hours }),
  { unit: ONE_DAY, label: intlDateRanges.days, length: 1, custom: true },
  ...fillTimes<MessageDescriptor>(29, { unit: ONE_DAY, label: intlDateRanges.days, length: 2 }),
  ...fillTimes<MessageDescriptor>(2, { unit: ONE_WEEK, label: intlDateRanges.weeks, custom: true }),
  ...fillTimes<MessageDescriptor>(2, {
    unit: ONE_WEEK,
    label: intlDateRanges.weeks,
    length: 3,
  }),
  ...fillTimes<MessageDescriptor>(12, {
    unit: ONE_DAY * 30,
    label: intlDateRanges.months,
  }),
] as const;

export const banMessages = defineMessages({
  email: { defaultMessage: 'Email', id: 'sy+pv5' },
  emailColon: { defaultMessage: 'Email:', id: 'xpTPb3' },
  pseudo: { defaultMessage: 'Nickname', id: 'SBkvpf' },
  pseudoColon: { defaultMessage: 'Username:', id: 'Zc7Wun' },
  ip: { defaultMessage: 'IP Address', id: 'uJV0wU' },
  ipColon: { defaultMessage: 'IP address:', id: '/7I9Fi' },
  account: { defaultMessage: 'Account', id: 'TwyMau' },
  accountColon: { defaultMessage: 'Account:', id: '8qq26q' },
  blockIPWarning: {
    defaultMessage:
      "Blocking a user's IP address may inadvertently block another user sharing the same IP.",
    id: 'XwXJXb',
  },
});
export const BAN_TYPES = [
  { value: 'email', title: banMessages.emailColon },
  { value: 'pseudo', title: banMessages.pseudoColon },
  { value: 'ip', title: banMessages.ipColon, warning: banMessages.blockIPWarning },
] as const;

export const defaultBanType = {
  value: 'account',
  title: banMessages.accountColon,
} as const;

type ApiLegacyPseudoBanType = 'username';

export type BanTypeKey =
  | (typeof BAN_TYPES)[number]['value']
  | (typeof defaultBanType)['value']
  | ApiLegacyPseudoBanType;

export const intlReasons = defineMessages({
  title: { defaultMessage: 'Reason for ban:', id: 'k8RHEy' },
  reason: { defaultMessage: 'Reason:', id: 'DC6oNZ' },
  reasonDesc: { defaultMessage: 'Reason description:', id: 'B93NlR' },
});
